import { motion } from "framer-motion";
import { Chapter, Reveal, MaskLines, ArrowLink, stagger } from "./Reveal";
import NetworkGraphic from "./NetworkGraphic";
import { Compass, PenTool, Target, Search, Filter } from "lucide-react";

const SERVICES = [
  {
    icon: Compass,
    title: "Brand Strategy & Positioning",
    body: "Defining how a brand looks, sounds, and stands out in a crowded market.",
  },
  {
    icon: PenTool,
    title: "Content Creation",
    body: "Copywriting, social content, and campaign assets built to convert, not just decorate a feed.",
  },
  {
    icon: Target,
    title: "Paid Advertising",
    body: "Performance-driven social and search advertising, built around measurable ROI.",
  },
  {
    icon: Search,
    title: "SEO & Organic Growth",
    body: "Long-term visibility and search performance that compounds over time.",
  },
  {
    icon: Filter,
    title: "Full-Funnel Consulting",
    body: "End-to-end digital strategy connecting brand, content, and paid growth into one coherent plan.",
  },
];

const cardVariant = {
  hidden: { opacity: 0, y: 30 },
  show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] } },
};

export default function Media() {
  return (
    <section
      id="media"
      data-testid="media-section"
      className="relative border-y border-hairline bg-surface/30"
    >
      <div className="pointer-events-none absolute right-0 top-16 h-[380px] w-[380px] rounded-full bg-steel/12 blur-[150px]" />
      <div className="mx-auto max-w-[1400px] px-6 py-28 lg:px-12 lg:py-40">
        <Chapter index="02" label="Al Yakhour Media" className="mb-10" />
        <div className="grid grid-cols-1 items-center gap-14 lg:grid-cols-2 lg:gap-20">
          <Reveal className="order-2 lg:order-1">
            <NetworkGraphic
              variant="media"
              label="Brand · Content · Growth"
              badge="Agency"
              className="aspect-[4/3] w-full"
            />
          </Reveal>
          <div className="order-1 lg:order-2">
            <h2 className="font-display text-4xl font-extrabold leading-[1.05] tracking-tighter text-cloud sm:text-5xl">
              <MaskLines lines={["Marketing &", "digital consulting."]} />
            </h2>
            <Reveal delay={0.15}>
              <p className="mt-8 max-w-xl font-body text-base font-light leading-relaxed text-fog sm:text-lg">
                Al Yakhour Media is our marketing and digital agency arm. We partner
                with brands across strategy, content, and growth — helping businesses
                show up sharper and scale faster across every channel that matters.
              </p>
            </Reveal>
            <Reveal delay={0.25} className="mt-10">
              <ArrowLink href="mailto:media@alyakhour.com" testid="media-contact-btn">
                media@alyakhour.com
              </ArrowLink>
            </Reveal>
          </div>
        </div>

        <Reveal delay={0.1}>
          <div
            data-testid="media-marketing-division"
            className="mt-16 overflow-hidden rounded-sm border border-steel/40 bg-surface p-8 sm:p-10 lg:mt-24"
          >
            <p className="mb-3 font-body text-xs font-semibold uppercase tracking-[0.3em] text-steel-light">
              Division
            </p>
            <h3 className="font-display text-2xl font-bold tracking-tight text-cloud sm:text-3xl">
              Al Yakhour Media &amp; Marketing
            </h3>
            <p className="mt-4 max-w-3xl font-body text-base font-light leading-relaxed text-fog">
              Full-service digital marketing, brand growth, and multi-channel
              reputation management. We help local and international brands scale
              their presence, manage local SEO, and optimize customer engagement
              across digital platforms.
            </p>
          </div>
        </Reveal>

        <motion.div
          className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3"
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-60px" }}
          variants={stagger(0.1)}
        >
          {SERVICES.map((s, i) => (
            <motion.article
              key={s.title}
              variants={cardVariant}
              data-testid={`media-service-card-${i + 1}`}
              className="group relative flex flex-col rounded-sm border border-hairline bg-surface p-7 transition-colors duration-500 hover:border-steel hover:bg-surface-hover"
            >
              <span className="mb-6 flex h-11 w-11 items-center justify-center rounded-sm border border-hairline text-steel-light transition-colors duration-500 group-hover:border-steel group-hover:text-cloud">
                <s.icon size={20} strokeWidth={1.75} />
              </span>
              <h3 className="mb-3 font-display text-lg font-bold tracking-tight text-cloud">
                {s.title}
              </h3>
              <p className="font-body text-sm font-light leading-relaxed text-fog">
                {s.body}
              </p>
            </motion.article>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
